import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { uploadAssignmentCoverImage } from '../../services/assignmentCoverImageStorageService';
import { CHALLENGE_THUMB_ASPECT, challengeThemeStyle } from '../../types/assignmentChallenge';
import { cropRectToWebp, readFileAsDataURL, type CropAreaPixels } from '../../utils/imageCompress';
import { readClipboardImageBlob } from '../../utils/slideImageCompress';

const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

const OUT_W = 800;
const OUT_H = Math.round(OUT_W / CHALLENGE_THUMB_ASPECT);

type Props = {
  open: boolean;
  currentUrl: string | null;
  onClose: () => void;
  onUploaded: (thumb: { url: string; path: string }) => void;
  onClear: () => void;
  themeColor?: string;
  title?: string;
};

const ChallengeThumbnailModal: React.FC<Props> = ({
  open,
  currentUrl,
  onClose,
  onUploaded,
  onClear,
  themeColor,
  title = '인증과제 썸네일',
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) return;
    setRawImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSaving(false);
    setError('');
    cropPixelsRef.current = null;
  }, [open]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 사용할 수 있습니다.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('15MB 이하 이미지만 사용할 수 있습니다.');
      return;
    }
    setError('');
    setRawImageSrc(await readFileAsDataURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
  }, []);

  useEffect(() => {
    if (!open || rawImageSrc) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File ? blob : new File([blob], 'clipboard.png', { type: blob.type || 'image/png' });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, rawImageSrc, handleFile]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, saving, onClose]);

  const save = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || saving) return;
    setSaving(true);
    setError('');
    try {
      const blob = await cropRectToWebp(rawImageSrc, cropPixelsRef.current, OUT_W, OUT_H, 0.8);
      const uploaded = await uploadAssignmentCoverImage(blob);
      onUploaded(uploaded);
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg === 'QUOTA_EXCEEDED'
          ? '이미지 저장 용량이 부족합니다.'
          : '썸네일 저장에 실패했습니다. 다시 시도해 주세요.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-member-photo-upload-overlay cp-chl-thumb-overlay"
      role="presentation"
      onClick={onClose}
      style={challengeThemeStyle(themeColor)}
    >
      <div
        className="cp-member-photo-upload-modal cp-chl-thumb-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-member-photo-upload-header">
          <h3>{title}</h3>
          <button type="button" className="cp-member-photo-upload-close" onClick={onClose} aria-label="닫기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
          </button>
        </div>
        {error ? (
          <p className="cp-member-photo-upload-error" role="alert">
            {error}
          </p>
        ) : null}
        {!rawImageSrc ? (
          <div
            className={`cp-chl-thumb-drop${isDragging ? ' is-dragging' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="썸네일 이미지 선택"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
          >
            {currentUrl ? <img src={currentUrl} alt="" className="cp-chl-thumb-drop-current" /> : null}
            <span className="cp-chl-thumb-drop-label">
              <span className="cp-chl-thumb-drop-plus" aria-hidden>
                +
              </span>
              클릭해서 이미지 선택 · 끌어다 놓기 · Ctrl+V(⌘V) 붙여넣기
              <small>16:9 비율로 자릅니다 · JPG/PNG/WebP</small>
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="cp-member-photo-upload-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleFile(file);
              }}
            />
          </div>
        ) : (
          <div className="cp-member-photo-upload-crop-wrap">
            <div className="cp-member-photo-upload-crop cp-chl-thumb-crop">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={CHALLENGE_THUMB_ASPECT}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_a, pixels) => {
                  cropPixelsRef.current = pixels;
                }}
              />
            </div>
            <label className="cp-member-photo-upload-zoom">
              <span>확대</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </label>
          </div>
        )}
        <div className="cp-member-photo-upload-footer">
          {rawImageSrc ? (
            <button
              type="button"
              className="cp-member-photo-upload-btn is-cancel"
              disabled={saving}
              onClick={() => {
                setRawImageSrc('');
                cropPixelsRef.current = null;
              }}
            >
              다시 선택
            </button>
          ) : currentUrl ? (
            <button
              type="button"
              className="cp-member-photo-upload-btn is-cancel"
              onClick={() => {
                onClear();
                onClose();
              }}
            >
              썸네일 없애기
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="cp-member-photo-upload-btn is-save"
            disabled={!rawImageSrc || saving}
            onClick={() => void save()}
          >
            {saving ? '저장 중…' : '적용'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChallengeThumbnailModal;
